import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, Pills } from "@/components/tools/fields";
import { SubHead } from "@/components/tools/blocks";

const LEADERSHIP = [
  "SEND is a visible priority within the School Improvement Plan.",
  "SEND is discussed regularly at Senior Leadership Team meetings.",
  "The SENDCO has sufficient time to fulfil statutory duties.",
  "Governors receive regular SEND reports.",
  "SEND funding is reviewed strategically.",
];
const PARTNERSHIP = [
  "Parents report feeling listened to.",
  "Communication is timely and respectful.",
  "Parents understand their child's support arrangements.",
  "School and home work collaboratively.",
  "Concerns are responded to appropriately.",
];
const REFLECT = [
  { k: "strongest", label: "Where are relationships strongest?" },
  { k: "barriers", label: "What barriers exist?" },
  { k: "improve", label: "What could improve communication?" },
];
const OTHER = [
  { id: "identification", title: "Identification", items: ["SEND register is accurate", "Pupils are reviewed regularly", "Graduated approach is consistent", "Evidence is recorded effectively", "Concerns from staff and parents are acted on promptly"] },
  { id: "staffing", title: "Staffing", items: ["Staff receive SEND training", "New staff receive induction", "Staff know pupil needs", "Staff understand reasonable adjustments", "Staff can access SEND advice and support when needed"] },
  { id: "outcomes", title: "Outcomes", items: ["Progress is evaluated", "Attendance is monitored", "Participation is reviewed", "Preparation for adulthood is considered", "Pupil and parent views inform the review of outcomes"] },
];
const SCALE = ["Not in Place", "Emerging", "Developing", "Established", "Embedded"];
const OPTS = [1, 2, 3, 4, 5].map((n) => ({ v: n, label: String(n) }));

const Statement = ({ id, text, value, onScore, evidence, onEvidence }) => (
  <li className="avoid-break flex flex-col gap-3 py-4">
    <span className="font-bold">{text}</span>
    <Pills legend={`Score for: ${text}`} name={id} options={OPTS} value={value} onChange={onScore} testid={id} />
    {onEvidence && <AutoTextarea id={`${id}-ev`} label="Evidence" value={evidence} onChange={onEvidence} testid={`${id}-evidence`} />}
  </li>
);

export const HealthCheck = () => {
  const [data, setData, reset] = useStoredState("health-check-v3", { s: {}, ev: {}, t: {} });
  const s = data.s || {};
  const setS = (k, v) => setData((d) => ({ ...d, s: { ...d.s, [k]: v } }));
  const setEv = (k, v) => setData((d) => ({ ...d, ev: { ...d.ev, [k]: v } }));
  const setT = (k, v) => setData((d) => ({ ...d, t: { ...d.t, [k]: v } }));
  const sum = (prefix, n) => Array.from({ length: n }, (_, i) => s[`${prefix}-${i}`] || 0).reduce((a, b) => a + b, 0);
  const sheet = [
    { label: "Leadership & Strategic Oversight", v: sum("lead", 5), max: 25 },
    { label: "Parent Partnership", v: sum("pp", 5), max: 25 },
    ...OTHER.map((o) => ({ label: o.title, v: sum(o.id, o.items.length), max: o.items.length * 5 })),
  ];
  const total = sheet.reduce((a, b) => a + b.v, 0);
  const max = sheet.reduce((a, b) => a + b.max, 0);
  return (
    <ToolShell id="health-check" onReset={reset}>
      <ul className="mb-6 flex flex-wrap gap-2" aria-label="Scoring scale">
        {SCALE.map((t, i) => <li key={t} className="rounded-full border-2 border-line px-3 py-1 text-sm"><span className="font-bold">{i + 1}</span> = {t}</li>)}
      </ul>

      <SubHead letter="A" title="Leadership & Strategic Oversight" />
      <ol className="divide-y divide-line">
        {LEADERSHIP.map((t, i) => (
          <Statement key={t} id={`hc-lead-${i}`} text={t} value={s[`lead-${i}`]} onScore={(v) => setS(`lead-${i}`, v)}
            evidence={data.ev?.[`lead-${i}`]} onEvidence={(v) => setEv(`lead-${i}`, v)} />
        ))}
      </ol>
      <p className="mt-2 font-bold" data-testid="hc-lead-score">Leadership Score: {sheet[0].v} / 25</p>
      <div className="mt-4"><AutoTextarea id="hc-lead-priority" label="Priority actions" rows={3} value={data.t?.leadPriority} onChange={(v) => setT("leadPriority", v)} testid="hc-lead-priority" /></div>

      <SubHead letter="B" title="Parent Partnership Health Check">Partnership working. Rate each statement 1 to 5.</SubHead>
      <ol className="divide-y divide-line">
        {PARTNERSHIP.map((t, i) => <Statement key={t} id={`hc-pp-${i}`} text={t} value={s[`pp-${i}`]} onScore={(v) => setS(`pp-${i}`, v)} />)}
      </ol>
      <h5 className="mt-6 font-bold">Reflection questions</h5>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        {REFLECT.map((r) => <AutoTextarea key={r.k} id={`hc-pp-${r.k}`} label={r.label} rows={3} value={data.t?.[r.k]} onChange={(v) => setT(r.k, v)} testid={`hc-pp-${r.k}`} />)}
      </div>
      <p className="mt-4 font-bold" data-testid="hc-pp-score">Overall Score: {sheet[1].v} / 25</p>

      <SubHead letter="C" title="Identification, Staffing and Outcomes" />
      <div className="grid gap-5 lg:grid-cols-3">
        {OTHER.map((o) => (
          <article key={o.id} className="avoid-break rounded-2xl border-2 border-line bg-surface p-5">
            <h5 className="font-display text-xl font-medium">{o.title}</h5>
            <ol className="divide-y divide-line">
              {o.items.map((t, i) => <Statement key={t} id={`hc-${o.id}-${i}`} text={t} value={s[`${o.id}-${i}`]} onScore={(v) => setS(`${o.id}-${i}`, v)} />)}
            </ol>
            <AutoTextarea id={`hc-${o.id}-notes`} label="Notes" value={data.t?.[o.id]} onChange={(v) => setT(o.id, v)} testid={`hc-${o.id}-notes`} />
          </article>
        ))}
      </div>

      <div className="avoid-break mt-8 rounded-2xl bg-brand p-6 text-brand-fg sm:p-8" aria-live="polite" data-testid="hc-score-sheet">
        <h4 className="font-display text-2xl font-medium">SENDCO Score Sheet</h4>
        <dl className="mt-4">
          {sheet.map((r) => (
            <div key={r.label} className="flex justify-between border-b py-2.5" style={{ borderColor: "color-mix(in srgb, currentColor 25%, transparent)" }}>
              <dt className="font-bold">{r.label}</dt><dd>{r.v} / {r.max}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 flex items-end justify-between">
          <span className="font-bold">Total</span>
          <span className="font-display text-6xl font-medium leading-none" data-testid="hc-score">{total}<span className="text-2xl opacity-80"> /{max}</span></span>
        </div>
      </div>
    </ToolShell>
  );
};
